(() => {
  document.documentElement.classList.remove('no-js');

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const themeConfig = window.RasvenisTheme || {};
  const themeRoutes = themeConfig.routes || {};
  const defaultDiscountCode = String(themeConfig.discountCode || '').trim();
  const formatMoney = (cents) => new Intl.NumberFormat(themeConfig.locale || 'tr-TR', {
    style: 'currency',
    currency: themeConfig.currency || 'TRY'
  }).format(Number(cents || 0) / 100);

  function trackRvEvent(name, detail = {}) {
    const eventName = `rv_${String(name || 'event').replace(/[^a-z0-9_]/gi, '_').toLowerCase()}`;
    const payload = { ...detail, currency: detail.currency || themeConfig.currency || 'TRY' };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, rv_ecommerce: payload });
    document.dispatchEvent(new CustomEvent(`rasvenis:${name}`, { detail: payload }));
  }

  function initCopyButtons(context = document) {
    qsa('[data-copy-code]', context).forEach((button) => {
      if (button.dataset.rvReady === 'true') return;
      button.dataset.rvReady = 'true';
      button.addEventListener('click', async () => {
        const code = String(button.dataset.copyCode || '').trim();
        if (!code) return;
        try {
          sessionStorage.setItem('rv-active-discount', code);
        } catch (_) {}
        trackRvEvent('coupon_copy', { coupon: code, page_path: window.location.pathname });
        try {
          await navigator.clipboard.writeText(code);
          const old = button.innerHTML;
          button.innerHTML = `${code} <small>KOPYALANDI</small>`;
          window.setTimeout(() => { button.innerHTML = old; }, 1600);
        } catch (_) {
          const input = document.createElement('input');
          input.value = code;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
          const old = button.innerHTML;
          button.innerHTML = `${code} <small>KOPYALANDI</small>`;
          window.setTimeout(() => { button.innerHTML = old; }, 1600);
        }
      });
    });
  }

  function initMenu(context = document) {
    const header = context.matches?.('[data-header]') ? context : qs('[data-header]', context);
    if (!header || header.dataset.rvReady === 'true') return;
    const toggle = qs('[data-menu-toggle]', header);
    const menu = qs('[data-mobile-menu]', header);
    if (!toggle || !menu) return;
    header.dataset.rvReady = 'true';
    const close = () => {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    };
    toggle.addEventListener('click', () => {
      const opening = menu.hidden;
      menu.hidden = !opening;
      toggle.setAttribute('aria-expanded', String(opening));
      document.body.classList.toggle('menu-open', opening);
      if (opening) {
        header.style.setProperty('--rv-menu-top', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
        qs('a, button, shopify-account', menu)?.focus({ preventScroll: true });
      }
    });
    qsa('a', menu).forEach((link) => link.addEventListener('click', close));
    window.addEventListener('resize', () => { if (window.innerWidth >= 990) close(); });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) {
        close();
        toggle.focus();
      }
    });
  }

  function initHeroSliders(context = document) {
    qsa('[data-hero-slider]', context).forEach((slider) => {
      if (slider.dataset.rvReady === 'true') return;
      slider.dataset.rvReady = 'true';
      const slides = qsa('[data-slide]', slider);
      const dots = qsa('[data-slider-dot]', slider);
      if (slides.length < 2) return;
      let current = 0;
      let timer;
      const show = (index) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === current);
          dot.setAttribute('aria-selected', String(i === current));
        });
      };
      const autoplay = () => {
        window.clearInterval(timer);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || document.hidden) return;
        timer = window.setInterval(() => show(current + 1), 6500);
      };
      qs('[data-slider-prev]', slider)?.addEventListener('click', () => { show(current - 1); autoplay(); });
      qs('[data-slider-next]', slider)?.addEventListener('click', () => { show(current + 1); autoplay(); });
      dots.forEach((dot) => dot.addEventListener('click', () => { show(Number(dot.dataset.sliderDot)); autoplay(); }));
      slider.addEventListener('mouseenter', () => window.clearInterval(timer));
      slider.addEventListener('mouseleave', autoplay);
      slider.addEventListener('focusin', () => window.clearInterval(timer));
      slider.addEventListener('focusout', autoplay);
      let touchX = 0;
      slider.addEventListener('touchstart', (event) => { touchX = event.touches[0].clientX; }, { passive: true });
      slider.addEventListener('touchend', (event) => {
        const diff = touchX - event.changedTouches[0].clientX;
        if (Math.abs(diff) > 45) show(current + (diff > 0 ? 1 : -1));
        autoplay();
      }, { passive: true });
      show(0);
      autoplay();
    });
  }

  function initGalleries(context = document) {
    qsa('[data-product-gallery]', context).forEach((gallery) => {
      if (gallery.dataset.rvReady === 'true') return;
      gallery.dataset.rvReady = 'true';

      const viewport = qs('[data-gallery-viewport]', gallery);
      const track = qs('[data-gallery-track]', gallery);
      const items = qsa('[data-gallery-item]', gallery);
      const thumbs = qsa('[data-gallery-thumb]', gallery);

      if (!viewport || !track || !items.length) return;

      let currentIndex = 0;
      let scrollFrame = 0;
      let scrollEndTimer = 0;
      let dragging = false;
      let dragStartX = 0;
      let dragStartScroll = 0;

      const clampIndex = (index) => Math.min(items.length - 1, Math.max(0, Number(index) || 0));

      const updateState = (index) => {
        currentIndex = clampIndex(index);
        const activeAspect = Number(items[currentIndex]?.dataset.galleryAspect || 0);
        if (activeAspect > 0) gallery.style.setProperty('--rv-gallery-aspect', String(activeAspect));

        items.forEach((item, itemIndex) => {
          const active = itemIndex === currentIndex;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-hidden', String(!active));
          if (!active) qs('video', item)?.pause();
        });

        thumbs.forEach((thumb) => {
          const thumbIndex = Number(thumb.dataset.galleryThumb);
          const active = thumbIndex === currentIndex;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-selected', String(active));
          thumb.tabIndex = active ? 0 : -1;
        });
      };

      const closestIndex = () => {
        const viewportCenter = viewport.scrollLeft + (viewport.clientWidth / 2);
        let closest = 0;
        let closestDistance = Infinity;

        items.forEach((item, index) => {
          const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
          const distance = Math.abs(itemCenter - viewportCenter);
          if (distance < closestDistance) {
            closest = index;
            closestDistance = distance;
          }
        });

        return closest;
      };

      const select = (index, behavior = 'smooth') => {
        const safeIndex = clampIndex(index);
        const target = items[safeIndex];
        if (!target) return;

        updateState(safeIndex);
        viewport.scrollTo({ left: target.offsetLeft, behavior });

        const activeThumb = thumbs.find((thumb) => Number(thumb.dataset.galleryThumb) === safeIndex);
        activeThumb?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
      };

      gallery.rvSelect = select;

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => select(Number(thumb.dataset.galleryThumb)));
      });

      viewport.addEventListener('scroll', () => {
        if (scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          updateState(closestIndex());
        });

        window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(() => {
          select(closestIndex(), 'smooth');
        }, 120);
      }, { passive: true });

      viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          select(currentIndex + 1);
        }
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          select(currentIndex - 1);
        }
        if (event.key === 'Home') {
          event.preventDefault();
          select(0);
        }
        if (event.key === 'End') {
          event.preventDefault();
          select(items.length - 1);
        }
      });

      /* Touch devices use native horizontal scrolling. This block adds mouse dragging on desktop. */
      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        dragStartX = event.clientX;
        dragStartScroll = viewport.scrollLeft;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      });

      viewport.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        viewport.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
      });

      const finishDrag = (event) => {
        if (!dragging) return;
        dragging = false;
        viewport.classList.remove('is-dragging');
        viewport.releasePointerCapture?.(event.pointerId);
        select(closestIndex(), 'smooth');
      };

      viewport.addEventListener('pointerup', finishDrag);
      viewport.addEventListener('pointercancel', finishDrag);
      viewport.addEventListener('lostpointercapture', () => {
        if (!dragging) return;
        dragging = false;
        viewport.classList.remove('is-dragging');
        select(closestIndex(), 'smooth');
      });

      qsa('img', gallery).forEach((image) => image.addEventListener('dragstart', (event) => event.preventDefault()));

      updateState(0);
      viewport.scrollLeft = 0;
    });
  }

  function initShippingTimers(context = document) {
    qsa('[data-shipping-timer]', context).forEach((timer) => {
      if (timer.dataset.rvReady === 'true') return;
      timer.dataset.rvReady = 'true';
      const isBusinessDay = (date) => date.getDay() !== 0 && date.getDay() !== 6;
      const nextBusinessDay = (date, offset = 1) => {
        const result = new Date(date);
        let added = 0;
        while (added < offset) {
          result.setDate(result.getDate() + 1);
          if (isBusinessDay(result)) added += 1;
        }
        return result;
      };
      const nextShippingCutoff = (date, cutoffHour) => {
        const target = new Date(date);
        target.setHours(cutoffHour, 0, 0, 0);
        if (isBusinessDay(date) && date < target) return target;
        do {
          target.setDate(target.getDate() + 1);
          target.setHours(cutoffHour, 0, 0, 0);
        } while (!isBusinessDay(target));
        return target;
      };
      const isSameDate = (first, second) => (
        first.getFullYear() === second.getFullYear()
        && first.getMonth() === second.getMonth()
        && first.getDate() === second.getDate()
      );
      const update = () => {
        const cutoffHour = Number(timer.dataset.cutoff || 14);
        const now = new Date();
        const shippingCutoff = nextShippingCutoff(now, cutoffHour);
        const title = qs('[data-shipping-title]', timer);
        const detail = qs('[data-shipping-detail]', timer);
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (title) {
          if (isSameDate(now, shippingCutoff)) {
            title.textContent = 'Bugün kargoda';
          } else if (isSameDate(tomorrow, shippingCutoff)) {
            title.textContent = 'Yarın kargoda';
          } else {
            const weekdayName = new Intl.DateTimeFormat('tr-TR', { weekday: 'long' }).format(shippingCutoff);
            title.textContent = `${weekdayName.charAt(0).toUpperCase()}${weekdayName.slice(1)} kargoda`;
          }
        }

        const totalSeconds = Math.max(0, Math.floor((shippingCutoff - now) / 1000));
        const daysLeft = Math.floor(totalSeconds / 86400);
        const hoursLeft = Math.floor((totalSeconds % 86400) / 3600);
        const minutesLeft = Math.floor((totalSeconds % 3600) / 60);
        const secondsLeft = totalSeconds % 60;

        if (detail) {
          const dayText = daysLeft > 0 ? `${daysLeft} gün ` : '';
          detail.textContent = `${dayText}${hoursLeft} sa ${minutesLeft} dk içinde sipariş ver.`;
        }

        const setCountdownValue = (selector, value) => {
          const node = qs(selector, timer);
          if (node) node.textContent = String(value).padStart(2, '0');
        };
        setCountdownValue('[data-shipping-days]', daysLeft);
        setCountdownValue('[data-shipping-hours]', hoursLeft);
        setCountdownValue('[data-shipping-minutes]', minutesLeft);
        setCountdownValue('[data-shipping-seconds]', secondsLeft);

        const deliveryNode = qs('[data-delivery-date]', timer);
        if (deliveryNode) {
          const deliveryStart = nextBusinessDay(shippingCutoff, 1);
          const deliveryEnd = nextBusinessDay(deliveryStart, 1);
          const formatter = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' });
          deliveryNode.textContent = `${formatter.format(deliveryStart)} – ${formatter.format(deliveryEnd)}`;
        }
      };
      update();
      const hasLiveCountdown = Boolean(qs('[data-shipping-seconds]', timer));
      window.setInterval(update, hasLiveCountdown ? 1000 : 60000);
    });
  }

  function initCampaignCountdowns(context = document) {
    qsa('[data-campaign-countdown]', context).forEach((campaign) => {
      if (campaign.dataset.rvReady === 'true') return;
      campaign.dataset.rvReady = 'true';
      const endValue = campaign.dataset.campaignEnd;
      if (!endValue) return;
      const end = new Date(endValue);
      if (Number.isNaN(end.getTime())) return;
      const fields = {
        days: qs('[data-campaign-days]', campaign),
        hours: qs('[data-campaign-hours]', campaign),
        minutes: qs('[data-campaign-minutes]', campaign),
        seconds: qs('[data-campaign-seconds]', campaign)
      };
      const set = (node, value) => { if (node) node.textContent = String(value).padStart(2, '0'); };
      const update = () => {
        const distance = end.getTime() - Date.now();
        if (distance <= 0) {
          campaign.hidden = true;
          window.clearInterval(campaign.rvCountdown);
          return;
        }
        set(fields.days, Math.floor(distance / 86400000));
        set(fields.hours, Math.floor((distance % 86400000) / 3600000));
        set(fields.minutes, Math.floor((distance % 3600000) / 60000));
        set(fields.seconds, Math.floor((distance % 60000) / 1000));
      };
      update();
      campaign.rvCountdown = window.setInterval(update, 1000);
    });
  }

  function initFomoData(context = document) {
    const toNumber = (value, fallback = 0) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const randomInt = (min, max) => {
      const low = Math.ceil(Math.min(min, max));
      const high = Math.floor(Math.max(min, max));
      return Math.floor(Math.random() * (high - low + 1)) + low;
    };
    const hashText = (value) => {
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return (hash >>> 0).toString(36);
    };
    const animate = (node, value) => {
      if (!node || !Number.isFinite(Number(value))) return;
      const end = Math.round(Number(value));
      const startValue = Number(node.textContent) || 0;
      if (startValue === end) return;
      const startedAt = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - startedAt) / 420);
        const eased = 1 - Math.pow(1 - progress, 3);
        node.textContent = String(Math.round(startValue + (end - startValue) * eased));
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    qsa('[data-fomo-root]', context).forEach((root) => {
      if (root.dataset.fomoReady === 'true') return;
      root.dataset.fomoReady = 'true';

      const range = (name, fallbackMin, fallbackMax, floor = 0) => {
        let min = toNumber(root.dataset[`fomo${name}Min`], fallbackMin);
        let max = toNumber(root.dataset[`fomo${name}Max`], fallbackMax);
        if (min > max) [min, max] = [max, min];
        min = Math.max(floor, Math.round(min));
        max = Math.max(min, Math.round(max));
        return { min, max };
      };

      const viewersRange = range('Viewers', 7, 14);
      const cartRange = range('Cart', 2, 5);
      const soldRange = range('Sold', 18, 28);
      const stockRange = range('Stock', 8, 12);
      const viewerDelay = range('Change', 9, 20, 1);
      const cartDelay = range('CartChange', 25, 55, 1);
      const soldDelay = range('SoldChange', 60, 120, 1);
      const persistMinutes = clamp(Math.round(toNumber(root.dataset.fomoPersistMinutes, 60)), 1, 10080);
      const persistMs = persistMinutes * 60000;
      const allowRealStock = root.dataset.fomoUseRealStock !== 'false';
      let realStock = allowRealStock && root.dataset.fomoRealStock === 'true';
      let actualStock = Math.max(0, Math.round(toNumber(root.dataset.fomoStockActual, 0)));
      const editorMode = Boolean(window.Shopify && window.Shopify.designMode);

      const configSignature = [
        viewersRange.min, viewersRange.max,
        cartRange.min, cartRange.max,
        soldRange.min, soldRange.max,
        stockRange.min, stockRange.max,
        viewerDelay.min, viewerDelay.max,
        cartDelay.min, cartDelay.max,
        soldDelay.min, soldDelay.max,
        persistMinutes,
        allowRealStock ? 1 : 0,
        realStock ? 1 : 0,
        actualStock
      ].join('|');
      const productKey = root.dataset.productKey || window.location.pathname;
      const storageKey = `rv-fomo-v3-${productKey}-${hashText(configSignature)}`;

      const getStored = () => {
        if (editorMode) return null;
        try {
          const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
          if (!saved || saved.signature !== configSignature || saved.expiresAt < Date.now()) return null;
          return saved;
        } catch (_) {
          return null;
        }
      };
      const save = (state) => {
        if (editorMode) return;
        try {
          localStorage.setItem(storageKey, JSON.stringify({
            ...state,
            signature: configSignature,
            expiresAt: Date.now() + persistMs
          }));
        } catch (_) {}
      };

      const minimumViewerForCart = Math.min(viewersRange.max, Math.max(viewersRange.min, cartRange.min + 1));
      const stored = getStored();
      const state = {
        viewers: clamp(
          Math.round(toNumber(stored?.viewers, randomInt(minimumViewerForCart, viewersRange.max))),
          minimumViewerForCart,
          viewersRange.max
        ),
        cart: clamp(
          Math.round(toNumber(stored?.cart, randomInt(cartRange.min, cartRange.max))),
          cartRange.min,
          cartRange.max
        ),
        sold: clamp(
          Math.round(toNumber(stored?.sold, randomInt(soldRange.min, soldRange.max))),
          soldRange.min,
          soldRange.max
        ),
        stock: realStock
          ? actualStock
          : clamp(
              Math.round(toNumber(stored?.stock, randomInt(stockRange.min, stockRange.max))),
              stockRange.min,
              stockRange.max
            )
      };

      const cartCap = () => Math.max(
        0,
        Math.min(cartRange.max, state.viewers - 1)
      );
      state.cart = clamp(state.cart, Math.min(cartRange.min, cartCap()), cartCap());

      const render = () => {
        state.viewers = clamp(Math.round(state.viewers), minimumViewerForCart, viewersRange.max);
        state.cart = clamp(Math.round(state.cart), Math.min(cartRange.min, cartCap()), cartCap());
        state.sold = clamp(Math.round(state.sold), soldRange.min, soldRange.max);
        state.stock = realStock
          ? Math.max(0, Math.round(actualStock))
          : clamp(Math.round(state.stock), stockRange.min, stockRange.max);

        qsa('[data-fomo-viewers]', root).forEach((node) => animate(node, state.viewers));
        qsa('[data-fomo-cart]', root).forEach((node) => animate(node, state.cart));
        qsa('[data-fomo-sold]', root).forEach((node) => animate(node, state.sold));
        qsa('[data-fomo-stock]', root).forEach((node) => animate(node, state.stock));

        const stockMax = realStock ? Math.max(1, actualStock) : Math.max(1, stockRange.max);
        const percent = clamp(Math.round((state.stock / stockMax) * 100), 0, 100);
        qsa('[data-stock-meter-fill]', root).forEach((node) => { node.style.width = `${percent}%`; });
        save(state);
      };

      const boundedStep = (current, limits, weights = { down: 0.25, same: 0.35 }) => {
        if (limits.min === limits.max) return limits.min;
        if (current <= limits.min) return current + 1;
        if (current >= limits.max) return current - 1;
        const roll = Math.random();
        if (roll < weights.down) return current - 1;
        if (roll < weights.down + weights.same) return current;
        return current + 1;
      };

      render();

      const endpoint = root.dataset.fomoEndpoint?.trim();
      if (endpoint) {
        const refresh = async () => {
          if (!root.isConnected) return;
          try {
            const response = await fetch(endpoint, {
              headers: { Accept: 'application/json' },
              cache: 'no-store'
            });
            if (!response.ok) return;
            const data = await response.json();
            if (Number.isFinite(Number(data.viewers))) {
              state.viewers = clamp(Math.round(Number(data.viewers)), minimumViewerForCart, viewersRange.max);
            }
            if (Number.isFinite(Number(data.cart_count))) {
              state.cart = clamp(Math.round(Number(data.cart_count)), Math.min(cartRange.min, cartCap()), cartCap());
            }
            if (Number.isFinite(Number(data.sold_24h))) {
              state.sold = clamp(Math.round(Number(data.sold_24h)), soldRange.min, soldRange.max);
            }
            if (Number.isFinite(Number(data.stock))) {
              actualStock = Math.max(0, Math.round(Number(data.stock)));
              state.stock = actualStock;
            }
            render();
          } catch (_) {
            /* Son doğrulanmış değerler korunur. */
          }
        };
        refresh();
        window.setInterval(refresh, 30000);
        return;
      }

      const schedule = (delayRange, task) => {
        const arm = () => {
          if (!root.isConnected) return;
          const delay = randomInt(delayRange.min, delayRange.max) * 1000;
          window.setTimeout(() => {
            if (!root.isConnected) return;
            if (!document.hidden) task();
            arm();
          }, delay);
        };
        arm();
      };

      schedule(viewerDelay, () => {
        state.viewers = boundedStep(state.viewers, {
          min: minimumViewerForCart,
          max: viewersRange.max
        }, { down: 0.27, same: 0.28 });
        state.cart = Math.min(state.cart, cartCap());
        render();
      });

      schedule(cartDelay, () => {
        state.cart = boundedStep(state.cart, {
          min: Math.min(cartRange.min, cartCap()),
          max: cartCap()
        }, { down: 0.25, same: 0.45 });
        render();
      });

      schedule(soldDelay, () => {
        state.sold = boundedStep(state.sold, soldRange, { down: 0.12, same: 0.38 });
        render();
      });

      const stockDelay = {
        min: Math.max(30, soldDelay.min),
        max: Math.max(Math.max(30, soldDelay.min), soldDelay.max)
      };
      schedule(stockDelay, () => {
        if (!realStock && state.stock > stockRange.min && Math.random() < 0.35) {
          state.stock -= 1;
          render();
        }
      });

      root.addEventListener('rv:variant-change', (event) => {
        const detail = event.detail || {};
        realStock = allowRealStock && Boolean(detail.inventoryManaged);
        actualStock = Math.max(0, Math.round(toNumber(detail.stock, 0)));
        root.dataset.fomoRealStock = String(realStock);
        root.dataset.fomoStockActual = String(actualStock);
        state.stock = realStock
          ? actualStock
          : clamp(state.stock || randomInt(stockRange.min, stockRange.max), stockRange.min, stockRange.max);
        render();
      });
    });
  }

  function initSalesNotifications(context = document) {
    const pop = context.matches?.('[data-sale-pop]') ? context : qs('[data-sale-pop]', context);
    if (!pop || pop.dataset.rvReady === 'true') return;
    pop.dataset.rvReady = 'true';
    try {
      if (sessionStorage.getItem('rv-sale-pop-muted') === '1') return;
    } catch (_) {}
    let entries = qsa('[data-sale-entry]').map((node) => ({
      name: node.dataset.name,
      city: node.dataset.city,
      product: node.dataset.product,
      time: node.dataset.time
    })).filter((entry) => entry.name && entry.city);
    let index = 0;
    let hideTimer;
    let loopTimer;
    const fill = (entry) => {
      const values = {
        '[data-sale-name]': entry.name,
        '[data-sale-city]': entry.city,
        '[data-sale-product]': entry.product || 'Rasvenis SPF 50+',
        '[data-sale-time]': entry.time || 'Az önce'
      };
      Object.entries(values).forEach(([selector, value]) => { const node = qs(selector, pop); if (node) node.textContent = value; });
    };
    const hide = () => {
      pop.classList.remove('is-visible');
      window.setTimeout(() => { pop.hidden = true; }, 350);
    };
    const show = () => {
      if (!entries.length) return;
      fill(entries[index % entries.length]);
      index += 1;
      pop.hidden = false;
      requestAnimationFrame(() => pop.classList.add('is-visible'));
      window.clearTimeout(hideTimer);
      hideTimer = window.setTimeout(hide, 5600);
    };
    const endpoint = pop.dataset.endpoint;
    if (endpoint) {
      fetch(endpoint, { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then((response) => response.ok ? response.json() : null)
        .then((data) => {
          if (!data) return;
          const live = Array.isArray(data) ? data : (Array.isArray(data.orders) ? data.orders : [data]);
          const valid = live.filter((entry) => entry && entry.name && entry.city);
          if (valid.length) entries = valid;
        })
        .catch(() => {});
    }
    const delay = Math.max(2, Number(pop.dataset.delay || 4)) * 1000;
    const interval = Math.max(10, Number(pop.dataset.interval || 20)) * 1000;
    window.setTimeout(() => {
      show();
      loopTimer = window.setInterval(show, interval);
    }, delay);
    qs('[data-sale-pop-close]', pop)?.addEventListener('click', () => {
      window.clearInterval(loopTimer);
      window.clearTimeout(hideTimer);
      hide();
      try { sessionStorage.setItem('rv-sale-pop-muted', '1'); } catch (_) {}
    });
  }

  function showToast(detail = 'Ürününüz sepette sizi bekliyor.', type = 'success') {
    const toast = qs('[data-cart-toast]');
    if (!toast) return;
    const detailNode = qs('[data-toast-detail]', toast);
    const titleNode = qs('strong', toast);
    const iconNode = qs('.rv-toast__icon', toast);
    const isError = type === 'error';
    if (detailNode) detailNode.textContent = detail;
    if (titleNode) titleNode.textContent = isError ? 'Sepet güncellenemedi' : 'Sepete eklendi';
    if (iconNode) iconNode.textContent = isError ? '!' : '✓';
    toast.classList.toggle('is-error', isError);
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-visible');
    window.clearTimeout(window.rvToastTimer);
    window.rvToastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 4200);
  }

  function setCartCount(itemCount) {
    const count = Math.max(0, Number(itemCount) || 0);
    qsa('[data-cart-count]').forEach((node) => { node.textContent = String(count); });
    qsa('.rv-cart-link').forEach((link) => {
      link.setAttribute('aria-label', `Sepet, ${count} ürün`);
    });
  }

  async function updateCartCount() {
    const response = await fetch(themeRoutes.cartJson || '/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) return;
    const cart = await response.json();
    setCartCount(cart.item_count);
  }

  function getActiveDiscountCode(fallback = '') {
    try {
      return String(sessionStorage.getItem('rv-active-discount') || fallback || '').trim();
    } catch (_) {
      return String(fallback || '').trim();
    }
  }

  function cartUrlFor(discountCode = '') {
    const cartPath = themeRoutes.cart || `${themeRoutes.root || '/'}cart`;
    const code = String(discountCode || '').trim();
    if (!code) return new URL(cartPath, window.location.origin).toString();

    const discountUrl = new URL(
      `${themeRoutes.root || '/'}discount/${encodeURIComponent(code)}`,
      window.location.origin
    );
    discountUrl.searchParams.set('redirect', cartPath);
    return discountUrl.toString();
  }

  let activeCartAddRequest = null;
  let activeCartNormalizationRequest = null;

  function cartApiUrl(action) {
    const base = String(themeRoutes.root || '/');
    return `${base}${base.endsWith('/') ? '' : '/'}cart/${action}.js`;
  }

  async function cartJsonRequest(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || 'Sepet güncellenemedi.');
    }
    return payload;
  }

  async function fetchCartSnapshot() {
    return cartJsonRequest(themeRoutes.cartJson || '/cart.js');
  }

  async function clearCartCompletely() {
    return cartJsonRequest(cartApiUrl('clear'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
  }

  async function addSingleCartLine(variantId, quantity) {
    return cartJsonRequest(themeRoutes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: String(variantId),
          quantity: Math.max(1, Number(quantity) || 1)
        }]
      })
    });
  }

  function cartMatchesExactSelection(cart, variantId, quantity) {
    const expectedVariantId = String(variantId);
    const expectedQuantity = Math.max(1, Number(quantity) || 1);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (items.length !== 1) return false;

    const line = items[0] || {};
    const lineVariantId = String(line.variant_id || line.id || '');
    const properties = line.properties && typeof line.properties === 'object' ? line.properties : {};

    return lineVariantId === expectedVariantId
      && Number(line.quantity) === expectedQuantity
      && Object.keys(properties).length === 0;
  }

  async function replaceCartWithExactSelection(variantId, quantity) {
    const safeQuantity = Math.max(1, Number(quantity) || 1);

    await clearCartCompletely();
    await addSingleCartLine(variantId, safeQuantity);
    let cart = await fetchCartSnapshot();

    // A third-party listener or an old hidden line property must never create a second line.
    if (!cartMatchesExactSelection(cart, variantId, safeQuantity)) {
      await clearCartCompletely();
      await addSingleCartLine(variantId, safeQuantity);
      cart = await fetchCartSnapshot();
    }

    if (!cartMatchesExactSelection(cart, variantId, safeQuantity)) {
      throw new Error('Sepet güvenli biçimde oluşturulamadı. Lütfen tekrar deneyin.');
    }

    return cart;
  }

  async function addToCartAndGoToCart({ variantId, quantity, discountCode = '', productTitle = '', unitPrice = 0, packageLabel = '' }) {
    if (activeCartAddRequest) return activeCartAddRequest;

    activeCartAddRequest = (async () => {
      const safeQuantity = Math.max(1, Number(quantity) || 1);
      const cart = await replaceCartWithExactSelection(variantId, safeQuantity);

      trackRvEvent('add_to_cart', {
        item_id: String(variantId),
        item_name: productTitle,
        quantity: safeQuantity,
        value: Math.round(Number(unitPrice || 0) * safeQuantity) / 100,
        coupon: String(discountCode || ''),
        package: packageLabel
      });
      setCartCount(cart.item_count);
      window.location.assign(cartUrlFor(discountCode));
    })();

    try {
      return await activeCartAddRequest;
    } catch (error) {
      activeCartAddRequest = null;
      throw error;
    }
  }

  async function normalizeSingleVariantCart({ bundle2Code = '', bundle3Code = '' } = {}) {
    if (activeCartNormalizationRequest) return activeCartNormalizationRequest;

    activeCartNormalizationRequest = (async () => {
      const cart = await fetchCartSnapshot();
      const items = Array.isArray(cart?.items) ? cart.items : [];
      if (!items.length) return false;

      const variantIds = [...new Set(items.map((item) => String(item.variant_id || item.id || '')))].filter(Boolean);
      if (variantIds.length !== 1) return false;

      const hasDuplicateLines = items.length > 1;
      const hasLineProperties = items.some((item) => Object.keys(item.properties || {}).length > 0);
      if (!hasDuplicateLines && !hasLineProperties) return false;

      const intendedQuantity = Math.max(1, ...items.map((item) => Number(item.quantity) || 1));
      const variantId = variantIds[0];
      await replaceCartWithExactSelection(variantId, intendedQuantity);

      const discountCode = intendedQuantity >= 3
        ? String(bundle3Code || '').trim()
        : intendedQuantity === 2
          ? String(bundle2Code || '').trim()
          : getActiveDiscountCode(defaultDiscountCode);

      window.location.replace(cartUrlFor(discountCode));
      return true;
    })();

    try {
      return await activeCartNormalizationRequest;
    } finally {
      activeCartNormalizationRequest = null;
    }
  }

  function initProducts(context = document) {
    qsa('[data-product-root]', context).forEach((root) => {
      if (root.dataset.rvReady === 'true') return;
      root.dataset.rvReady = 'true';

      let unitPrice = Number(root.dataset.unitPrice || 0);
      let unitComparePrice = Number(root.dataset.comparePrice || 0);
      const purchaseControl = qs('[data-rv-purchase-control]', root);
      const quantityInput = qs('[data-form-quantity]', root);
      const variantInput = qs('[data-variant-id]', root);
      const currentPrice = qs('[data-current-price]', root);
      const comparePrice = qs('[data-compare-price]', root);
      const savingBadge = qs('[data-saving-badge]', root);
      const firstOrderPrice = qs('[data-first-order-price]', root);
      const firstOrderDiscount = Math.min(50, Math.max(0, Number(root.dataset.firstOrderDiscount || 0)));
      const buttonPrice = qs('[data-button-price]', root);
      const buyNowButton = qs('[data-buy-now]', root);
      const gallery = qs('[data-product-gallery]', root);
      const clampDiscount = (value) => Math.min(100, Math.max(0, Number(value) || 0));

      const updateBundle = () => {
        const activeChoice = qs('[data-bundle-quantity]:checked', root);
        const quantity = Number(activeChoice?.dataset.bundleQuantity || 1);
        const discount = clampDiscount(activeChoice?.dataset.bundleDiscount || 0);
        const total = Math.round(unitPrice * quantity * (100 - discount) / 100);

        if (quantityInput) quantityInput.value = quantity;
        if (buttonPrice) buttonPrice.textContent = formatMoney(total);

        qsa('[data-bundle-price]', root).forEach((node) => {
          const choice = qs('[data-bundle-quantity]', node.closest('.rv-bundle'));
          const itemQuantity = Number(choice?.dataset.bundleQuantity || 1);
          const itemDiscount = clampDiscount(choice?.dataset.bundleDiscount || 0);
          node.textContent = formatMoney(Math.round(unitPrice * itemQuantity * (100 - itemDiscount) / 100));
        });

        qsa('.rv-bundle', root).forEach((label) => {
          label.classList.toggle('is-active', Boolean(activeChoice && label.contains(activeChoice)));
        });

        const regularUnitPrice = unitComparePrice > unitPrice ? unitComparePrice : unitPrice;
        const regularTotal = Math.round(regularUnitPrice * quantity);
        root.dispatchEvent(new CustomEvent('rv:bundle-change', {
          detail: {
            quantity,
            discount,
            discountCode: String(activeChoice?.dataset.bundleCode || '').trim(),
            unitPrice,
            comparePrice: unitComparePrice,
            total,
            regularTotal
          }
        }));
      };

      qsa('[data-bundle-quantity]', root).forEach((choice) => {
        choice.addEventListener('change', () => {
          updateBundle();
          trackRvEvent('select_package', {
            item_id: String(root.dataset.currentVariant || ''),
            item_name: root.dataset.productTitle || '',
            quantity: Number(choice.dataset.bundleQuantity || 1),
            discount_percent: clampDiscount(choice.dataset.bundleDiscount || 0),
            coupon: String(choice.dataset.bundleCode || '')
          });
        });
      });

      const variantSelect = qs('[data-variant-select]', root);
      const updateVariant = () => {
        const option = variantSelect?.selectedOptions?.[0];
        if (!option) {
          updateBundle();
          return;
        }

        unitPrice = Number(option.dataset.price || unitPrice);
        const compareAt = Number(option.dataset.comparePrice || 0);
        unitComparePrice = compareAt;
        const available = option.dataset.available === 'true';
        const stock = Math.max(0, Number(option.dataset.stock || 0));
        const inventoryManaged = option.dataset.inventoryManaged === 'true';
        const buyLabel = buyNowButton?.dataset.buyLabel || 'Hemen Satın Al';
        const soldOutLabel = buyNowButton?.dataset.soldOutLabel || 'Tükendi';

        root.dataset.unitPrice = String(unitPrice);
        if (variantInput) variantInput.value = option.value;
        if (currentPrice) currentPrice.textContent = formatMoney(unitPrice);
        if (firstOrderPrice) {
          firstOrderPrice.textContent = formatMoney(Math.round(unitPrice * (100 - firstOrderDiscount) / 100));
        }

        if (comparePrice) {
          comparePrice.textContent = formatMoney(compareAt);
          comparePrice.hidden = !(compareAt > unitPrice);
        }
        if (savingBadge) savingBadge.hidden = !(compareAt > unitPrice);

        if (buyNowButton) {
          buyNowButton.disabled = !available;
          const label = qs('[data-buy-now-label]', buyNowButton);
          if (label) label.textContent = available ? buyLabel : soldOutLabel;
        }

        const mediaIndex = Number(option.dataset.mediaIndex || 0);
        const galleryOffset = Number(gallery?.dataset.galleryCoverOffset || 0);
        if (mediaIndex > 0 && gallery?.rvSelect) gallery.rvSelect((mediaIndex - 1) + galleryOffset);

        if (root.dataset.productUrl && window.location.pathname.includes('/products/')) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', url);
        }

        root.dataset.currentVariant = option.value;
        root.dataset.currentAvailable = String(available);
        root.dataset.productImage = option.dataset.imageSrc || root.dataset.productImage || '';
        root.dataset.productImageAlt = option.dataset.imageAlt || root.dataset.productImageAlt || root.dataset.productTitle || '';

        root.dispatchEvent(new CustomEvent('rv:variant-change', {
          detail: {
            stock,
            inventoryManaged,
            available,
            variantId: option.value,
            variantTitle: option.dataset.variantTitle || '',
            productTitle: root.dataset.productTitle || '',
            unitPrice,
            compareAt,
            imageSrc: option.dataset.imageSrc || root.dataset.productImage || '',
            imageAlt: option.dataset.imageAlt || root.dataset.productImageAlt || root.dataset.productTitle || ''
          }
        }));

        updateBundle();
      };

      if (variantSelect) {
        variantSelect.addEventListener('change', updateVariant);
        updateVariant();
      } else {
        updateBundle();
      }

      trackRvEvent('view_item', {
        item_id: String(root.dataset.currentVariant || ''),
        item_name: root.dataset.productTitle || '',
        value: unitPrice / 100,
        page_path: window.location.pathname
      });

      if (purchaseControl && buyNowButton) {
        const handlePurchase = async (event) => {
          event?.preventDefault?.();
          event?.stopImmediatePropagation?.();
          const button = buyNowButton;
          if (!button || button.disabled || root.dataset.rvCartBusy === 'true') return;

          const label = qs('[data-buy-now-label]', button);
          const originalLabel = button.dataset.buyLabel || label?.textContent?.trim() || 'Hemen Satın Al';
          const soldOutLabel = button.dataset.soldOutLabel || 'Tükendi';
          const activeChoice = qs('[data-bundle-quantity]:checked', root);
          const selectedQuantity = Math.max(1, Math.floor(Number(quantityInput?.value || activeChoice?.dataset.bundleQuantity || 1)));
          const selectedDiscount = clampDiscount(activeChoice?.dataset.bundleDiscount || 0);
          const bundleDiscountCode = String(activeChoice?.dataset.bundleCode || '').trim();
          const selectedDiscountCode = bundleDiscountCode || (selectedQuantity === 1
            ? getActiveDiscountCode(defaultDiscountCode)
            : '');
          const selectedVariantId = String(variantInput?.value || root.dataset.currentVariant || '').trim();

          if (!selectedVariantId) {
            showToast('Ürün varyantı bulunamadı.');
            return;
          }
          if (selectedQuantity > 1 && selectedDiscount > 0 && !selectedDiscountCode) {
            showToast('Bu paket için Shopify indirim kodu tanımlanmamış.');
            return;
          }

          root.dataset.rvCartBusy = 'true';
          button.disabled = true;
          if (label) label.textContent = 'Sepete Yönlendiriliyor…';

          try {
            await addToCartAndGoToCart({
              variantId: selectedVariantId,
              quantity: selectedQuantity,
              discountCode: selectedDiscountCode,
              productTitle: root.dataset.productTitle || '',
              unitPrice: Math.round(unitPrice * (100 - selectedDiscount) / 100),
              packageLabel: Number(activeChoice?.dataset.bundleQuantity || 0) === selectedQuantity
                ? (activeChoice?.closest('.rv-bundle')?.querySelector('strong')?.textContent?.trim() || `${selectedQuantity} adet`)
                : `${selectedQuantity} adet`
            });
          } catch (error) {
            delete root.dataset.rvCartBusy;
            if (label) label.textContent = 'Tekrar Dene';
            showToast(error.message || 'Bir hata oluştu.');
            button.disabled = false;

            window.setTimeout(() => {
              const selectedAvailable = variantSelect
                ? variantSelect.selectedOptions[0]?.dataset.available === 'true'
                : true;
              button.disabled = !selectedAvailable;
              if (label) label.textContent = selectedAvailable ? originalLabel : soldOutLabel;
            }, 1800);
          }
        };

        buyNowButton.addEventListener('click', handlePurchase, true);
      }
    });
  }

  function initMobileBuy() {
    const bar = qs('[data-mobile-buy]');
    const target = qs('[data-product-root]');
    const buyButton = target ? qs('[data-buy-now]', target) : null;

    const clearBarState = () => {
      if (!bar) return;
      bar.rvObserver?.disconnect?.();
      bar.rvObserver = null;
      if (bar.rvScrollHandler) window.removeEventListener('scroll', bar.rvScrollHandler);
      if (bar.rvResizeHandler) window.removeEventListener('resize', bar.rvResizeHandler);
      bar.rvScrollHandler = null;
      bar.rvResizeHandler = null;
      if (bar.rvRoot && bar.rvBundleHandler) bar.rvRoot.removeEventListener('rv:bundle-change', bar.rvBundleHandler);
      if (bar.rvRoot && bar.rvVariantHandler) bar.rvRoot.removeEventListener('rv:variant-change', bar.rvVariantHandler);
      if (bar.rvBuyButton && bar.rvClickHandler) bar.rvBuyButton.removeEventListener('click', bar.rvClickHandler);
      if (bar.rvMinusButton && bar.rvMinusHandler) bar.rvMinusButton.removeEventListener('click', bar.rvMinusHandler);
      if (bar.rvPlusButton && bar.rvPlusHandler) bar.rvPlusButton.removeEventListener('click', bar.rvPlusHandler);
    };

    if (!bar) {
      document.body.classList.remove('has-mobile-buy');
      return;
    }

    clearBarState();

    const stickyButton = qs('[data-sticky-buy]', bar);
    const minusButton = qs('[data-sticky-qty-minus]', bar);
    const plusButton = qs('[data-sticky-qty-plus]', bar);
    const quantityInput = qs('[data-sticky-quantity]', bar);
    const stickyPrice = qs('[data-sticky-price]', bar);
    const stickyComparePrice = qs('[data-sticky-compare-price]', bar);
    const stickyButtonPrice = qs('[data-sticky-buy-price]', bar);
    const stickyButtonLabel = qs('[data-sticky-buy-label]', bar);
    const stickyImage = qs('[data-sticky-image]', bar);
    const stickyTitle = qs('[data-sticky-title]', bar);
    const bundleChoices = target ? qsa('[data-bundle-quantity]', target) : [];

    const staticUnitPrice = () => Math.max(0, Number(bar.dataset.stickyUnitPrice || 0));
    const staticComparePrice = () => Math.max(0, Number(bar.dataset.stickyComparePrice || 0));
    const sanitizeQuantity = (value) => Math.max(1, Math.floor(Number(value) || 1));

    const activeQuantity = () => sanitizeQuantity(
      quantityInput?.value
      || bar.dataset.stickyQuantity
      || qs('[data-bundle-quantity]:checked', target)?.dataset.bundleQuantity
      || 1
    );

    const bundleSettings = (quantity) => {
      const choice = bundleChoices.find((item) => Number(item.dataset.bundleQuantity) === quantity);
      if (choice) {
        return {
          discount: Math.min(100, Math.max(0, Number(choice.dataset.bundleDiscount || 0))),
          discountCode: String(choice.dataset.bundleCode || '').trim()
        };
      }

      if (quantity === 2) {
        return {
          discount: Math.min(100, Math.max(0, Number(bar.dataset.stickyBundle2Discount || 0))),
          discountCode: String(bar.dataset.stickyBundle2Code || '').trim()
        };
      }
      if (quantity >= 3) {
        return {
          discount: Math.min(100, Math.max(0, Number(bar.dataset.stickyBundle3Discount || 0))),
          discountCode: String(bar.dataset.stickyBundle3Code || '').trim()
        };
      }
      return { discount: 0, discountCode: '' };
    };

    const updateQuantityControls = (quantity) => {
      const safeQuantity = sanitizeQuantity(quantity);
      if (quantityInput) quantityInput.value = String(safeQuantity);
      if (minusButton) minusButton.disabled = safeQuantity <= 1;
      if (plusButton) plusButton.disabled = false;
      bar.dataset.stickyQuantity = String(safeQuantity);
      return safeQuantity;
    };

    const syncAvailability = (available) => {
      const fallbackAvailable = bar.dataset.stickyAvailable !== 'false';
      const sourceAvailable = typeof available === 'boolean'
        ? available
        : target
          ? target.dataset.currentAvailable !== 'false'
          : fallbackAvailable;
      const isAvailable = Boolean(sourceAvailable) && (!buyButton || !buyButton.disabled);
      if (stickyButton) stickyButton.disabled = !isAvailable;
      if (stickyButtonLabel) {
        stickyButtonLabel.textContent = isAvailable
          ? (buyButton?.dataset.buyLabel || 'Hemen Satın Al')
          : (buyButton?.dataset.soldOutLabel || 'Tükendi');
      }
      bar.dataset.stickyAvailable = String(isAvailable);
    };

    const syncBundle = (detail = {}) => {
      const quantity = updateQuantityControls(detail.quantity || activeQuantity());
      const settings = bundleSettings(quantity);
      const unitPrice = Number.isFinite(Number(detail.unitPrice)) && Number(detail.unitPrice) > 0
        ? Number(detail.unitPrice)
        : target
          ? Math.max(0, Number(target.dataset.unitPrice || staticUnitPrice()))
          : staticUnitPrice();
      const compareAt = Number.isFinite(Number(detail.comparePrice))
        ? Math.max(0, Number(detail.comparePrice))
        : target
          ? Math.max(0, Number(target.dataset.comparePrice || staticComparePrice()))
          : staticComparePrice();
      const discount = Number.isFinite(Number(detail.discount))
        ? Math.min(100, Math.max(0, Number(detail.discount)))
        : settings.discount;
      const calculatedTotal = Math.round(unitPrice * quantity * (100 - discount) / 100);
      const total = Number.isFinite(Number(detail.total)) && Number(detail.total) > 0
        ? Number(detail.total)
        : calculatedTotal;
      const regularUnitPrice = compareAt > unitPrice ? compareAt : unitPrice;
      const regularTotal = Number.isFinite(Number(detail.regularTotal)) && Number(detail.regularTotal) > 0
        ? Number(detail.regularTotal)
        : Math.round(regularUnitPrice * quantity);
      const totalText = formatMoney(total);

      if (stickyPrice) stickyPrice.textContent = totalText;
      if (stickyButtonPrice) stickyButtonPrice.textContent = totalText;
      if (stickyComparePrice) {
        const showCompare = regularTotal > total;
        stickyComparePrice.hidden = !showCompare;
        if (showCompare) stickyComparePrice.textContent = formatMoney(regularTotal);
      }

      bar.dataset.stickyUnitPrice = String(unitPrice);
      bar.dataset.stickyComparePrice = String(compareAt);
      bar.dataset.stickyDiscount = String(discount);
      bar.dataset.stickyDiscountCode = String(detail.discountCode || settings.discountCode || '').trim();
      syncAvailability();
    };

    const selectQuantity = (quantity) => {
      const safeQuantity = updateQuantityControls(quantity);
      const choice = bundleChoices.find((item) => Number(item.dataset.bundleQuantity) === safeQuantity);
      const productQuantityInput = target ? qs('[data-form-quantity]', target) : null;
      if (productQuantityInput) productQuantityInput.value = String(safeQuantity);

      if (choice && !choice.checked) {
        choice.checked = true;
        choice.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        syncBundle({ quantity: safeQuantity });
      }
      return safeQuantity;
    };

    const syncVariant = (detail = {}) => {
      const available = typeof detail.available === 'boolean'
        ? detail.available
        : target
          ? target.dataset.currentAvailable !== 'false'
          : bar.dataset.stickyAvailable !== 'false';
      const imageSrc = detail.imageSrc || target?.dataset.productImage || '';
      const imageAlt = detail.imageAlt || target?.dataset.productImageAlt || target?.dataset.productTitle || '';

      if (stickyImage && imageSrc) {
        stickyImage.src = imageSrc;
        stickyImage.srcset = '';
        stickyImage.alt = imageAlt;
      }
      if (stickyTitle) stickyTitle.textContent = detail.productTitle || target?.dataset.productTitle || stickyTitle.textContent;
      if (detail.variantId || target?.dataset.currentVariant) {
        bar.dataset.stickyVariantId = String(detail.variantId || target.dataset.currentVariant);
      }
      if (Number.isFinite(Number(detail.unitPrice)) && Number(detail.unitPrice) > 0) {
        bar.dataset.stickyUnitPrice = String(Number(detail.unitPrice));
      }
      if (Number.isFinite(Number(detail.compareAt))) {
        bar.dataset.stickyComparePrice = String(Math.max(0, Number(detail.compareAt)));
      }
      syncAvailability(available);
    };

    const hideBar = () => {
      bar.hidden = true;
      bar.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('has-mobile-buy');
    };

    const showBar = () => {
      bar.hidden = false;
      bar.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-mobile-buy');
      syncVariant();
      syncBundle();
    };

    bar.rvRoot = target;
    bar.rvBuyButton = stickyButton;
    bar.rvMinusButton = minusButton;
    bar.rvPlusButton = plusButton;

    if (target) {
      bar.rvBundleHandler = (event) => syncBundle(event.detail || {});
      bar.rvVariantHandler = (event) => {
        syncVariant(event.detail || {});
        syncBundle(event.detail || {});
      };
      target.addEventListener('rv:bundle-change', bar.rvBundleHandler);
      target.addEventListener('rv:variant-change', bar.rvVariantHandler);
    }

    bar.rvMinusHandler = () => {
      selectQuantity(activeQuantity() - 1);
    };
    bar.rvPlusHandler = () => {
      selectQuantity(activeQuantity() + 1);
    };
    minusButton?.addEventListener('click', bar.rvMinusHandler);
    plusButton?.addEventListener('click', bar.rvPlusHandler);

    bar.rvClickHandler = () => {
      if (!stickyButton || stickyButton.disabled || activeCartAddRequest || target?.dataset.rvCartBusy === 'true') return;
      const quantity = selectQuantity(Number(quantityInput?.value || activeQuantity()));
      syncBundle({ quantity });

      if (buyButton && !buyButton.disabled) {
        buyButton.click();
        return;
      }

      const variantId = String(bar.dataset.stickyVariantId || '').trim();
      const discount = Math.max(0, Number(bar.dataset.stickyDiscount || 0));
      const bundleDiscountCode = String(bar.dataset.stickyDiscountCode || '').trim();
      const discountCode = bundleDiscountCode || (quantity === 1
        ? getActiveDiscountCode(defaultDiscountCode)
        : '');
      if (!variantId) {
        showToast('Ürün varyantı bulunamadı.');
        return;
      }
      if (quantity > 1 && discount > 0 && !discountCode) {
        showToast('Bu paket için Shopify indirim kodu tanımlanmamış.');
        return;
      }

      stickyButton.disabled = true;
      if (stickyButtonLabel) stickyButtonLabel.textContent = 'Sepete Yönlendiriliyor…';

      addToCartAndGoToCart({
        variantId,
        quantity,
        discountCode,
        productTitle: target?.dataset.productTitle || '',
        unitPrice: Math.round(Number(target?.dataset.unitPrice || 0) * (100 - discount) / 100),
        packageLabel: `${quantity} adet`
      }).catch((error) => {
        stickyButton.disabled = false;
        if (stickyButtonLabel) stickyButtonLabel.textContent = 'Tekrar Dene';
        showToast(error.message || 'Bir hata oluştu.');
      });
    };
    stickyButton?.addEventListener('click', bar.rvClickHandler);

    syncVariant();
    syncBundle();
    hideBar();

    const updateBarVisibility = () => {
      if (!target) {
        showBar();
        return;
      }

      const heroRect = target.getBoundingClientRect();
      if (heroRect.bottom <= 0) showBar();
      else hideBar();
    };

    if (target && 'IntersectionObserver' in window) {
      bar.rvObserver = new IntersectionObserver(updateBarVisibility, {
        threshold: [0, 0.01],
        rootMargin: '0px'
      });
      bar.rvObserver.observe(target);
    } else if (target) {
      bar.rvScrollHandler = updateBarVisibility;
      window.addEventListener('scroll', bar.rvScrollHandler, { passive: true });
    }

    bar.rvResizeHandler = updateBarVisibility;
    window.addEventListener('resize', bar.rvResizeHandler, { passive: true });
    updateBarVisibility();
  }

  function initCartEnhancements(context = document) {
    const roots = context.matches?.('[data-cart-root]')
      ? [context]
      : qsa('[data-cart-root]', context);

    roots.forEach((root) => {
      if (root.dataset.rvCartReady === 'true' || root.dataset.rvCartNormalize === 'checking') return;

      const sectionId = String(root.dataset.sectionId || '').trim();
      const bundle2Code = String(root.dataset.bundle2Code || '').trim();
      const bundle3Code = String(root.dataset.bundle3Code || '').trim();

      if (root.dataset.rvCartNormalize !== 'done') {
        root.dataset.rvCartNormalize = 'checking';
        root.classList.add('is-cart-loading');
        normalizeSingleVariantCart({ bundle2Code, bundle3Code })
          .then((changed) => {
            if (changed) return;
            root.dataset.rvCartNormalize = 'done';
            root.classList.remove('is-cart-loading');
            initCartEnhancements(root);
          })
          .catch((error) => {
            root.dataset.rvCartNormalize = 'done';
            root.classList.remove('is-cart-loading');
            showToast(error.message || 'Sepet kontrol edilemedi.', 'error');
            initCartEnhancements(root);
          });
        return;
      }

      root.dataset.rvCartReady = 'true';
      const cartForm = qs('[data-cart-form]', root);

      const cartChangeUrl = (() => {
        const base = String(themeRoutes.root || '/');
        return `${base}${base.endsWith('/') ? '' : '/'}cart/change.js`;
      })();

      const lineProperties = (line) => {
        try {
          const parsed = JSON.parse(String(line?.dataset.lineProperties || '{}')) || {};
          if (Array.isArray(parsed)) {
            return parsed.reduce((result, entry) => {
              if (Array.isArray(entry) && entry.length >= 2) {
                result[String(entry[0])] = entry[1];
              } else if (entry && typeof entry === 'object') {
                Object.assign(result, entry);
              }
              return result;
            }, {});
          }
          return parsed && typeof parsed === 'object' ? { ...parsed } : {};
        } catch (_) {
          return {};
        }
      };

      const codeForQuantity = (quantity) => {
        if (quantity >= 3 && bundle3Code) return bundle3Code;
        if (quantity === 2 && bundle2Code) return bundle2Code;
        return getActiveDiscountCode(defaultDiscountCode);
      };

      const propertiesForQuantity = (line) => {
        const properties = lineProperties(line);
        delete properties._discount_code;
        return properties;
      };

      const setBusy = (busy, element = root) => {
        root.classList.toggle('is-cart-loading', busy);
        element?.classList.toggle('is-loading', busy);
        qsa('button, input, textarea', element || root).forEach((control) => {
          if (busy) {
            control.dataset.rvWasDisabled = String(control.disabled);
            control.disabled = true;
          } else if (control.dataset.rvWasDisabled !== undefined) {
            control.disabled = control.dataset.rvWasDisabled === 'true';
            delete control.dataset.rvWasDisabled;
          }
        });
      };

      const refreshSection = async () => {
        if (!sectionId) {
          window.location.reload();
          return;
        }
        const cartUrl = new URL(themeRoutes.cart || window.location.pathname, window.location.origin);
        cartUrl.searchParams.set('section_id', sectionId);
        const response = await fetch(cartUrl.toString(), {
          headers: { Accept: 'text/html' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Sepet görünümü güncellenemedi.');
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const incoming = qs('[data-cart-root]', documentFragment);
        if (!incoming) {
          window.location.reload();
          return;
        }
        root.replaceWith(incoming);
        initShippingTimers(incoming);
        initCartEnhancements(incoming);
      };

      const changeLine = async ({ key, quantity, properties = null, element = null, redirectCode = '' }) => {
        if (!key) return;
        setBusy(true, element || root);
        try {
          const payload = { id: key, quantity: Math.max(0, Number(quantity) || 0) };
          if (properties) payload.properties = properties;
          const response = await fetch(cartChangeUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload)
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.description || data.message || 'Sepet güncellenemedi.');

          const code = String(redirectCode || codeForQuantity(payload.quantity) || '').trim();
          if (code) {
            try { sessionStorage.setItem('rv-active-discount', code); } catch (_) {}
          }

          if (Number.isFinite(Number(data.item_count))) setCartCount(data.item_count);
          else await updateCartCount();
          if (redirectCode) {
            window.location.assign(cartUrlFor(redirectCode));
            return true;
          }
          await refreshSection();
          return true;
        } catch (error) {
          setBusy(false, element || root);
          showToast(error.message || 'Sepet güncellenemedi.', 'error');
          return false;
        }
      };

      qsa('[data-cart-line]', root).forEach((line) => {
        const key = String(line.dataset.lineKey || '').trim();
        const quantityInput = qs('[data-cart-quantity]', line);
        const minusButton = qs('[data-cart-minus]', line);
        const plusButton = qs('[data-cart-plus]', line);
        const removeButton = qs('[data-cart-remove]', line);
        const currentQuantity = () => Math.max(1, Number(quantityInput?.value || 1));

        minusButton?.addEventListener('click', () => {
          const quantity = Math.max(1, currentQuantity() - 1);
          changeLine({
            key,
            quantity,
            properties: propertiesForQuantity(line, quantity),
            element: line
          });
        });

        plusButton?.addEventListener('click', () => {
          const quantity = currentQuantity() + 1;
          changeLine({
            key,
            quantity,
            properties: propertiesForQuantity(line, quantity),
            element: line
          });
        });

        removeButton?.addEventListener('click', (event) => {
          event.preventDefault();
          changeLine({ key, quantity: 0, element: line });
        });
      });

      qs('[data-cart-upsell]', root)?.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        const key = String(button.dataset.lineKey || '').trim();
        const quantity = Math.max(2, Number(button.dataset.targetQuantity || 2));
        const line = qsa('[data-cart-line]', root).find((item) => item.dataset.lineKey === key);
        const code = codeForQuantity(quantity);
        button.textContent = 'Paket Güncelleniyor…';
        const success = await changeLine({
          key,
          quantity,
          properties: propertiesForQuantity(line, quantity),
          element: qs('[data-cart-upsell-card]', root),
          redirectCode: code
        });
        if (!success && document.contains(button)) button.textContent = '2’li Pakete Geç';
      });

      cartForm?.addEventListener('submit', () => {
        const checkoutButton = qs('[name="checkout"]', cartForm);
        const estimatedTotal = Number(checkoutButton?.dataset.checkoutValue || 0) / 100;
        trackRvEvent('begin_checkout', { value: estimatedTotal, page_path: window.location.pathname });
        if (checkoutButton) {
          checkoutButton.setAttribute('aria-busy', 'true');
          checkoutButton.classList.add('is-loading');
          const label = qs('span', checkoutButton);
          if (label) label.textContent = 'Ödemeye Yönlendiriliyor…';
        }
      });
    });
  }

  function initQuickBuy() {
    const bar = qs('[data-quick-buy]');
    const target = qs('[data-product-root]');
    if (!bar || !target || bar.dataset.rvReady === 'true' || !('IntersectionObserver' in window)) return;
    bar.dataset.rvReady = 'true';
    const observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
    }, { threshold: 0.05 });
    observer.observe(target);
  }

  function initUgcVideos(context = document) {
    qsa('[data-ugc-section]', context).forEach((section) => {
      if (section.dataset.ugcReady === 'true') return;
      section.dataset.ugcReady = 'true';
      const track = qs('[data-ugc-track]', section);
      const cards = qsa('[data-ugc-card]', section);
      const current = qs('[data-ugc-current]', section);

      const prepareVideo = (card) => {
        const video = qs('[data-ugc-video] video', card);
        if (!video) return null;
        if (video.dataset.ugcPrepared !== 'true') {
          video.dataset.ugcPrepared = 'true';
          video.preload = 'metadata';
          try { video.load(); } catch (_) {}
        }
        return video;
      };

      const closeVideo = (card, reset = true) => {
        const panel = qs('[data-ugc-video]', card);
        const cover = qs('[data-ugc-play]', card);
        const video = qs('video', panel || card);
        if (video) {
          video.pause();
          if (reset) {
            try { video.currentTime = 0; } catch (_) {}
          }
        }
        if (panel) panel.hidden = true;
        if (cover) cover.hidden = false;
      };

      cards.forEach((card) => {
        const play = qs('[data-ugc-play]', card);
        const panel = qs('[data-ugc-video]', card);
        if (!play || !panel) return;
        play.addEventListener('click', () => {
          cards.forEach((other) => { if (other !== card) closeVideo(other); });
          const video = prepareVideo(card);
          if (!video) {
            showToast('Video şu anda yüklenemedi.', 'error');
            return;
          }
          play.hidden = true;
          panel.hidden = false;
          trackRvEvent('ugc_play', { person: card.querySelector('.rv-ugc-card__caption strong')?.textContent?.trim() || '' });
          const promise = video.play();
          if (promise?.catch) promise.catch(() => {});
        });
        qs('[data-ugc-close]', card)?.addEventListener('click', () => closeVideo(card));
        qs('video', panel)?.addEventListener('ended', () => closeVideo(card));
      });

      const cardStep = () => {
        const card = cards[0];
        if (!card) return 0;
        const styles = window.getComputedStyle(track);
        return card.getBoundingClientRect().width + (Number.parseFloat(styles.columnGap || styles.gap) || 0);
      };
      const scroll = (direction) => track?.scrollBy({ left: cardStep() * direction, behavior: 'smooth' });
      qs('[data-ugc-prev]', section)?.addEventListener('click', () => scroll(-1));
      qs('[data-ugc-next]', section)?.addEventListener('click', () => scroll(1));
      track?.addEventListener('scroll', () => {
        if (!current || !cardStep()) return;
        const index = Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / cardStep())));
        current.textContent = String(index + 1).padStart(2, '0');
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        const preloadObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            prepareVideo(entry.target);
            observer.unobserve(entry.target);
          });
        }, { root: null, rootMargin: '320px 120px', threshold: 0.01 });
        cards.forEach((card) => preloadObserver.observe(card));

        const visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) closeVideo(entry.target, false);
          });
        }, { root: null, threshold: 0.2 });
        cards.forEach((card) => visibilityObserver.observe(card));
      } else {
        cards.forEach(prepareVideo);
      }
    });
  }

  function initFaq(context = document) {
    qsa('.rv-faq__items', context).forEach((group) => {
      if (group.dataset.rvReady === 'true') return;
      group.dataset.rvReady = 'true';
      qsa('details', group).forEach((item) => item.addEventListener('toggle', () => {
        if (!item.open) return;
        qsa('details', group).forEach((other) => { if (other !== item) other.open = false; });
      }));
    });
  }

  function initTheme(context = document) {
    const initializers = [
      ['copy-buttons', initCopyButtons],
      ['menu', initMenu],
      ['hero-sliders', initHeroSliders],
      ['galleries', initGalleries],
      ['shipping-timers', initShippingTimers],
      ['campaign-countdowns', initCampaignCountdowns],
      ['fomo-data', initFomoData],
      ['sales-notifications', initSalesNotifications],
      ['products', initProducts],
      ['cart-enhancements', initCartEnhancements],
      ['ugc-videos', initUgcVideos],
      ['faq', initFaq]
    ];

    initializers.forEach(([name, initializer]) => {
      try {
        initializer(context);
      } catch (error) {
        console.error(`[RasvenisTheme] ${name} başlatılamadı:`, error);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initTheme();
    initMobileBuy();
    initQuickBuy();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initTheme(event.target);
    initMobileBuy();
    initQuickBuy();
  });
})();
